/**
 * TriggerSettingsRenderer - Renders trigger configuration UI in the canvas editor
 *
 * Reuses existing trigger settings components from the legacy AutomationTriggerCard
 * to maintain consistency and avoid code duplication.
 */

import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { useEventLogging } from '@AppModels/Logging';
import { useAdminUserSession } from '../../../../../models/hooks/adminStateHooks';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import {
	useAutomationTemplateAddTriggerMutation,
	useAutomationTemplateAllowUnsubscribedMutation,
	useAutomationTemplateSendToHouseholdMutation,
	useCancelCriteriaContactFilterMutation,
} from '../../../../../queries';
import { AdvancedFiltersContextProvider } from '../../../../components-v2/AdvancedFilters/AdvancedFiltersContext';
import { AdvancedFiltersLegacyUI } from '../../../../components/AdvancedFilters/AdvancedFiltersLegacyUI';
import { Checkbox } from '../../../../components/Checkbox';
import { Collapsible } from '../../../../components/Collapsible';
import { FlyoutModal } from '../../../../components/FlyoutModal';
import { AdvancedOptionsButton } from '../../../../components/automation/AutomationStepCard/presentation';
import {
	AutomationTriggerSettings,
	AutomationCancelOnReplyCheckbox,
} from '../../../../components/automation/AutomationTriggerCard/presentation';
import type { TriggerNodeData } from '../../types/triggerNodes';
import { useAutomationFlowContext } from '../../contexts/AutomationFlowContext';
import { styleSheet } from './styles/triggerSettingsRendererStyles';

interface TriggerSettingsRendererProps {
	data: TriggerNodeData;
	readOnly?: boolean;
}

export function TriggerSettingsRenderer({ data, readOnly }: TriggerSettingsRendererProps) {
	const userSession = useAdminUserSession();
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('TriggerSettingsRenderer');

	// Use live template from context (updates on mutations) with fallback to node data
	const flowContext = useAutomationFlowContext();
	const rawTemplate = flowContext.automationTemplate ?? data.automationTemplate;
	const impersonationContext = flowContext.impersonationContext ?? data.impersonationContext;

	// Ensure canEdit defaults to true for editing mode (parity with legacy list view)
	// The API may not return canEdit, causing checkboxes to be disabled
	type TemplateWithPermissions = Api.IAutomationTemplate & { canEdit?: boolean };
	const automationTemplate = useMemo((): TemplateWithPermissions | undefined => {
		if (!rawTemplate) {
			return rawTemplate;
		}
		return {
			...rawTemplate,
			canEdit: (rawTemplate as TemplateWithPermissions).canEdit ?? true,
		};
	}, [rawTemplate]);

	// Only consider readonly if explicitly set to false, not if undefined
	const isReadOnly = readOnly || automationTemplate?.canEdit === false;

	// Local state
	const [trigger, setTriggerState] = useState<Api.IAutomationTrigger | undefined>(data.automationTrigger);
	const [advancedOptionsToggled, setAdvancedOptionsToggled] = useState(false);
	const [advancedCancelOptions, setAdvancedCancelOptions] = useState(false);
	const [cancelCriteria, setCancelCriteria] = useState<Api.IContactFilterCriteria[]>([]);
	const [viewCriteriaFlyout, setViewCriteriaFlyout] = useState(false);

	// Mutations
	const addTriggerMutation = useAutomationTemplateAddTriggerMutation({
		onError: error => {
			logApiError('AddTrigger-Error', error);
			errorMessages.pushApiError(error);
		},
		onSuccess: () => {
			toast.success('Automation trigger set successfully');
		},
	});

	const sendToHouseholdMutation = useAutomationTemplateSendToHouseholdMutation({
		onError: error => {
			logApiError('SendToHousehold-Error', error);
			errorMessages.pushApiError(error);
		},
		onSuccess: () => {
			toast.success('Send to household setting updated successfully');
		},
	});

	const allowUnsubscribedMutation = useAutomationTemplateAllowUnsubscribedMutation({
		onError: error => {
			logApiError('AllowUnsubscribed-Error', error);
			errorMessages.pushApiError(error);
		},
		onSuccess: () => {
			toast.success('Allow unsubscribed setting updated successfully');
		},
	});

	const updateCriteriaMutation = useCancelCriteriaContactFilterMutation({
		impersonationContext,
		onSuccess: () => {
			toast.success('Automation cancel criteria updated successfully');
		},
		onError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
			logApiError('UpdateCancelCriteria-Error', error);
		},
	});

	// Sync trigger state with data prop
	useEffect(() => {
		const templateTrigger = automationTemplate?.draft?.trigger || automationTemplate?.published?.trigger;
		if (templateTrigger) {
			setTriggerState(templateTrigger);
		}
	}, [automationTemplate?.draft?.trigger, automationTemplate?.published?.trigger]);

	// Initialize advanced options state from template
	useEffect(() => {
		if (automationTemplate?.contactFilter?.criteria?.length) {
			setAdvancedOptionsToggled(true);
			setAdvancedCancelOptions(true);
			setCancelCriteria(automationTemplate.contactFilter.criteria);
		}
	}, [automationTemplate?.contactFilter?.criteria]);

	// Handler for setting trigger
	const handleSetTrigger = useCallback(
		async (newTrigger: Api.IAutomationTrigger, fallbackTrigger?: Api.IAutomationTrigger) => {
			if (!Api.isValidAutomationTrigger(newTrigger)) {
				setTriggerState(newTrigger);
				return;
			}

			if (!automationTemplate?.id) {
				return;
			}

			try {
				await addTriggerMutation.mutateAsync({
					id: automationTemplate.id,
					trigger: newTrigger,
					impersonationContext,
				});
				setTriggerState(newTrigger);
			} catch {
				if (fallbackTrigger) {
					setTriggerState(fallbackTrigger);
				}
			}
		},
		[automationTemplate?.id, addTriggerMutation, impersonationContext]
	);

	// Handler for send to household checkbox
	const handleSendToHouseholdChange = useCallback(
		(ev: React.ChangeEvent<HTMLInputElement>) => {
			if (!automationTemplate?.id) {
				return;
			}
			sendToHouseholdMutation.mutate({
				id: automationTemplate.id,
				sendToHousehold: ev.target.checked,
				impersonationContext,
			});
		},
		[automationTemplate?.id, sendToHouseholdMutation, impersonationContext]
	);

	// Handler for allow unsubscribed checkbox
	const handleAllowUnsubscribedChange = useCallback(
		(ev: React.ChangeEvent<HTMLInputElement>) => {
			if (!automationTemplate?.id) {
				return;
			}
			allowUnsubscribedMutation.mutate({
				id: automationTemplate.id,
				allowUnsubscribed: ev.target.checked,
				impersonationContext,
			});
		},
		[automationTemplate?.id, allowUnsubscribedMutation, impersonationContext]
	);

	// Handler for advanced cancel options checkbox
	const handleAdvancedCancelOptionsChange = useCallback(() => {
		if (!automationTemplate?.id) {
			return;
		}

		const newValue = !advancedCancelOptions;
		setAdvancedCancelOptions(newValue);

		if (newValue && cancelCriteria.length === 0) {
			// Opening and no criteria - show flyout to add criteria
			setViewCriteriaFlyout(true);
		} else if (!newValue && cancelCriteria.length > 0) {
			// Unchecking with existing criteria - clear them
			updateCriteriaMutation.mutate({ id: automationTemplate.id, cancelCriteria: [] });
			setCancelCriteria([]);
		}
	}, [automationTemplate?.id, advancedCancelOptions, cancelCriteria.length, updateCriteriaMutation]);

	// Handler for saving cancel criteria
	const handleSaveCriteria = useCallback(
		(criteria: Api.IContactFilterCriteria[]) => {
			if (!automationTemplate?.id) {
				return;
			}

			setViewCriteriaFlyout(false);

			if (criteria.length === 0) {
				setAdvancedCancelOptions(false);
				return;
			}

			setCancelCriteria(criteria);
			updateCriteriaMutation.mutate({ id: automationTemplate.id, cancelCriteria: criteria });
			setAdvancedCancelOptions(true);
		},
		[automationTemplate?.id, updateCriteriaMutation]
	);

	// Handler for dismissing flyout
	const handleDismissFlyout = useCallback(() => {
		setViewCriteriaFlyout(false);
		if (cancelCriteria.length === 0) {
			setAdvancedCancelOptions(false);
		}
	}, [cancelCriteria.length]);

	const isLoaded = !!automationTemplate?.id;
	const householdsEnabled = impersonationContext
		? impersonationContext.account?.features?.households?.enabled
		: userSession.account.features.households?.enabled;

	const triggerHasOptions = Api.VmUtils.Automations.triggers.triggerHasOptions(trigger);
	const isManualTrigger = !trigger?._type;

	if (!isLoaded) {
		return (
			<div className={css(styleSheet.loadingContainer)}>
				<span>Loading trigger settings...</span>
			</div>
		);
	}

	return (
		<div className={css(styleSheet.container)}>
			{/* Trigger-specific settings */}
			{triggerHasOptions && (
				<AutomationTriggerSettings
					automationTemplate={automationTemplate}
					impersonationContext={impersonationContext}
					onRequestSetTrigger={isReadOnly ? undefined : handleSetTrigger}
					trigger={trigger}
				/>
			)}

			{/* Cancel on reply checkbox */}
			<div className={css(styleSheet.cancelOnReplySection)}>
				<AutomationCancelOnReplyCheckbox
					automationTemplate={automationTemplate}
					impersonationContext={impersonationContext}
				/>
			</div>

			{/* Send to households checkbox */}
			{householdsEnabled && (
				<div className={css(styleSheet.householdsSection)}>
					<Checkbox
						checked={automationTemplate?.sendToHousehold === true}
						disabled={sendToHouseholdMutation.isPending}
						id='trigger-send-to-household-checkbox'
						onChange={handleSendToHouseholdChange}
						type='large'
					>
						<div>
							<div className={css(styleSheet.checkboxText)}>Send to Households for this automation.</div>
						</div>
					</Checkbox>
				</div>
			)}

			{/* Advanced options */}
			<div className={css(styleSheet.advancedSection)}>
				<AdvancedOptionsButton
					isCollapsed={!advancedOptionsToggled}
					onClick={() => setAdvancedOptionsToggled(!advancedOptionsToggled)}
				/>

				<Collapsible isCollapsed={!advancedOptionsToggled}>
					<div className={css(styleSheet.advancedContent)}>
						{/* Cancel if no longer meets criteria */}
						<Checkbox
							id='trigger-advanced-cancel-options'
							checked={advancedCancelOptions}
							type='large'
							onChange={handleAdvancedCancelOptionsChange}
						>
							<div>
								<p className={css(styleSheet.advancedCheckboxText)}>Cancel if contact no longer meets criteria</p>
							</div>
						</Checkbox>

						{/* View criteria button */}
						{cancelCriteria.length > 0 && (
							<button className={css(styleSheet.viewCriteriaButton)} onClick={() => setViewCriteriaFlyout(true)}>
								View Criteria
							</button>
						)}

						{/* Allow unsubscribed (manual triggers only) */}
						{isManualTrigger && (
							<>
								<Checkbox
									checked={!!automationTemplate?.allowUnsubscribed}
									className={css(styleSheet.allowUnsubscribedCheckbox)}
									disabled={allowUnsubscribedMutation.isPending}
									id='trigger-allow-unsubscribed-checkbox'
									onChange={handleAllowUnsubscribedChange}
									type='large'
									aria-describedby='trigger-allow-unsubscribed-helper'
								>
									<div>
										<div className={css(styleSheet.checkboxText)}>Allow sending to unsubscribed contacts</div>
									</div>
								</Checkbox>
								<div id='trigger-allow-unsubscribed-helper' className={css(styleSheet.helperText)}>
									Enable ONLY for critical transactional communications to stay in compliance with CAN-SPAM.
								</div>
							</>
						)}
					</div>
				</Collapsible>
			</div>

			{/* Cancel criteria flyout modal */}
			<FlyoutModal useDefaultHeader={false} onRequestClose={handleDismissFlyout} isOpen={viewCriteriaFlyout}>
				<AdvancedFiltersContextProvider
					initialFilterCriteria={cancelCriteria}
					refresh={viewCriteriaFlyout}
					onSave={handleSaveCriteria}
				>
					<AdvancedFiltersLegacyUI impersonationContext={impersonationContext} onDismissFlyout={handleDismissFlyout} />
				</AdvancedFiltersContextProvider>
			</FlyoutModal>
		</div>
	);
}
